import { useState, useMemo } from 'react'
import styles from './OPList.module.css'

const MONEDAS = { USD: '$', EUR: '€', GBP: '£', COP: '$', JPY: '¥' }

function formatFecha(ts) {
    if (!ts) return '—'
    const d = new Date(parseInt(ts) * 1000)
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatValor(valor, moneda) {
    const sym = MONEDAS[moneda] || ''
    return `${sym}${Number(valor).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda || ''}`
}

function OPList({ ops, selectedOP, canManage, canDelete, onSelect, onEdit, onDelete, onNew }) {
    const [filters, setFilters] = useState({ op_numero: '', proveedor: '', fecha_desde: '', fecha_hasta: '', valor_min: '', valor_max: '' })
    const [showFilters, setShowFilters] = useState(false)

    const filtered = useMemo(() => {
        return ops.filter(op => {
            if (filters.op_numero && !op.op_numero.toLowerCase().includes(filters.op_numero.toLowerCase())) return false
            if (filters.proveedor && !op.proveedor.toLowerCase().includes(filters.proveedor.toLowerCase())) return false
            if (filters.fecha_desde && op.created_at < Math.floor(new Date(filters.fecha_desde).getTime() / 1000).toString()) return false
            if (filters.fecha_hasta) {
                const endTs = Math.floor(new Date(filters.fecha_hasta + 'T23:59:59').getTime() / 1000).toString()
                if (op.created_at > endTs) return false
            }
            if (filters.valor_min && op.valor_factura < parseFloat(filters.valor_min)) return false
            if (filters.valor_max && op.valor_factura > parseFloat(filters.valor_max)) return false
            return true
        })
    }, [ops, filters])

    const hasFilters = Object.values(filters).some(v => v !== '')

    const clearFilters = () => setFilters({ op_numero: '', proveedor: '', fecha_desde: '', fecha_hasta: '', valor_min: '', valor_max: '' })

    return (
        <div className={styles.container}>
            {/* Barra de búsqueda */}
            <div className={styles.searchBar}>
                <input
                    className={styles.searchInput}
                    placeholder="Buscar por OP..."
                    value={filters.op_numero}
                    onChange={e => setFilters(f => ({ ...f, op_numero: e.target.value }))}
                />
                <button
                    className={`${styles.btnFilter} ${showFilters || hasFilters ? styles.btnFilterActive : ''}`}
                    onClick={() => setShowFilters(v => !v)}
                    title="Filtros avanzados"
                >
                    ⚙
                </button>
                {hasFilters && (
                    <button className={styles.btnClear} onClick={clearFilters} title="Limpiar filtros">✕</button>
                )}
            </div>

            {/* Filtros avanzados */}
            {showFilters && (
                <div className={styles.filtersPanel}>
                    <div className={styles.filterRow}>
                        <label>Proveedor</label>
                        <input
                            placeholder="Buscar proveedor..."
                            value={filters.proveedor}
                            onChange={e => setFilters(f => ({ ...f, proveedor: e.target.value }))}
                        />
                    </div>
                    <div className={styles.filterRow}>
                        <label>Fecha desde</label>
                        <input
                            type="date"
                            value={filters.fecha_desde}
                            onChange={e => setFilters(f => ({ ...f, fecha_desde: e.target.value }))}
                        />
                    </div>
                    <div className={styles.filterRow}>
                        <label>Fecha hasta</label>
                        <input
                            type="date"
                            value={filters.fecha_hasta}
                            onChange={e => setFilters(f => ({ ...f, fecha_hasta: e.target.value }))}
                        />
                    </div>
                    <div className={styles.filterRow}>
                        <label>Valor mín.</label>
                        <input
                            type="number"
                            placeholder="0"
                            value={filters.valor_min}
                            onChange={e => setFilters(f => ({ ...f, valor_min: e.target.value }))}
                        />
                    </div>
                    <div className={styles.filterRow}>
                        <label>Valor máx.</label>
                        <input
                            type="number"
                            placeholder="Sin límite"
                            value={filters.valor_max}
                            onChange={e => setFilters(f => ({ ...f, valor_max: e.target.value }))}
                        />
                    </div>
                </div>
            )}

            {/* Conteo */}
            <div className={styles.countRow}>
                <span className={styles.count}>{filtered.length} orden{filtered.length !== 1 ? 'es' : ''}</span>
                {canManage && (
                    <button className={styles.btnNewSmall} onClick={onNew}>+ Nueva OP</button>
                )}
            </div>

            {/* Lista */}
            {filtered.length === 0 ? (
                <div className={styles.empty}>
                    <span>📋</span>
                    <p>{ops.length === 0 ? 'No hay órdenes de pedido. Crea la primera.' : 'Sin resultados para los filtros aplicados.'}</p>
                </div>
            ) : (
                <div className={styles.list}>
                    {filtered.map(op => (
                        <div
                            key={op.op_id}
                            className={`${styles.opCard} ${selectedOP?.op_id === op.op_id ? styles.opCardSelected : ''} ${op.estado === 'cerrada' ? styles.opCardCerrada : ''}`}
                            onClick={() => onSelect(op)}
                        >
                            <div className={styles.opHeader}>
                                <span className={styles.opNumero}>{op.op_numero}</span>
                                <span className={`${styles.opEstado} ${op.estado === 'cerrada' ? styles.estadoCerrada : styles.estadoActiva}`}>
                                    {op.estado}
                                </span>
                            </div>
                            <div className={styles.opProveedor}>{op.proveedor || <em>Sin proveedor</em>}</div>
                            <div className={styles.opMeta}>
                                <span title="Número de factura">📄 {op.numero_factura || '—'}</span>
                                <span title="Valor de factura">💵 {formatValor(op.valor_factura, op.moneda)}</span>
                            </div>
                            <div className={styles.opFecha}>{formatFecha(op.created_at)}</div>

                            {canManage && selectedOP?.op_id === op.op_id && (
                                <div className={styles.opActions} onClick={e => e.stopPropagation()}>
                                    <button className={styles.btnEdit} onClick={() => onEdit(op)}>✏ Editar</button>
                                    {canDelete && (
                                        <button className={styles.btnDel} onClick={() => onDelete(op)}>Eliminar</button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default OPList
